import React, { useState } from 'react';
import {
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  OutlinedInput,
  TextField,
} from '@mui/material';
import {
  EmailOutlined,
  LockOutlined,
  Search,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import {
  prepareMobileNumber,
  ValidateAccountNumber,
  ValidateAddress,
  ValidateEmail,
  ValidateInput,
  ValidateMobile,
} from '../../utils/ValidationUtils';
import {
  BLUE_GRAY_200,
  BLUE_GRAY_500,
  ERROR,
  GRAY_500,
  PRIMARY,
} from '../../constants/Colors';
import { t } from 'i18next';
import _ from 'lodash';

export interface BakeasyTextFieldProps {
  label?: string;
  type?: string;
  name?: string;
  value?: string;
  required?: boolean;
  disabled?: boolean;
  maxLength?: number;
  helperText?: string;
  showPassword?: boolean;
  fullWidth?: boolean;
  multiline?: true;
  isError?: false;
  rows?: number;
  placeHolder?: string;
  style?: React.CSSProperties;
  helperMessage?: string;
  regexPattern?: RegExp;
  InputProps?: any;
  isWorkNumber?: boolean;
  //hideRequiredStar?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onIconClick?: (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => void;
  onEnterClick?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
}

const startAdormentStyle = {
  paddingLeft: 0,
  paddingRight: 0,
  marginRight: 1,
  marginLeft: 1,
  fontSize: 18,
  color: BLUE_GRAY_500,
};

const endAdormentStyle = { marginRight: -1, color: BLUE_GRAY_500 };

function BakeasyTextField(props: BakeasyTextFieldProps) {
  const [helperText, setHelperText] = useState<string>(props?.helperText ?? '');
  const [error, setError] = useState<boolean>(false);

  function handleIconClick(event: React.MouseEvent<HTMLButtonElement>) {
    if (props.onIconClick) {
      props.onIconClick(event);
    }
  }

  if (props?.name === 'search') {
    return (
      <TextField
        fullWidth={props?.fullWidth === false ? false : true}
        variant="outlined"
        name={props.name}
        type={props.type}
        label={props.label}
        // label={
        //   props.hideRequiredStar
        //     ? props.label
        //     : props.required
        //       ? `${props.label} *`
        //       : props.label
        // }
        value={props.value}
        required={props.required === false ? false : true}
        helperText={helperText}
        error={error}
        disabled={props.disabled ? props.disabled : false}
        size={props?.name === 'search' ? 'small' : 'medium'}
        multiline={props?.multiline ? props?.multiline : false}
        rows={props?.rows}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          validate(event.target);
          if (props?.onChange) props.onChange(event);
        }}
        onKeyUp={(event: React.KeyboardEvent<HTMLInputElement>) => {
          if (event?.key === 'Enter' && props?.onEnterClick)
            props?.onEnterClick(event);
        }}
        inputProps={{ maxLength: props?.maxLength ? props?.maxLength : 100 }}
        InputProps={{
          endAdornment: getEndAdorment(props?.name),
          style: { borderRadius: 8 },
        }}
        // InputLabelProps={{
        //   required: !props.hideRequiredStar,
        // }}
      />
    );
  } else if (props?.name === 'replymsg') {
    return (
      <TextField
        fullWidth={props?.fullWidth === false ? false : true}
        variant="outlined"
        name={props.name}
        type={props.type}
        label={props.label}
        value={props.value}
        helperText={helperText}
        error={error}
        disabled={props.disabled ? props.disabled : false}
        size={props?.name === 'replymsg' ? 'small' : 'medium'}
        multiline={props?.multiline ? props?.multiline : false}
        rows={props?.rows}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          validate(event.target);
          if (props?.onChange) props.onChange(event);
        }}
        onKeyUp={(event: React.KeyboardEvent<HTMLInputElement>) => {
          if (event?.key === 'Enter' && props?.onEnterClick)
            props?.onEnterClick(event);
        }}
        inputProps={{ maxLength: props?.maxLength ? props?.maxLength : 100 }}
        InputProps={{
          endAdornment: getEndAdorment(props?.name),
          style: { borderRadius: 8 },
        }}
      />
    );
  } else {
    return (
      <FormControl fullWidth={props?.fullWidth === false ? false : true}>
        <FormLabel
          sx={{
            fontWeight: 'bold',
            fontSize: '0.95rem',
            marginBottom: 0.5,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {props.label}
          {props?.required ? ' *' : ''}
          {/* {props?.required && <span style={{ color: 'red' }}>{'*'}</span>} */}
        </FormLabel>

        <OutlinedInput
          id={props?.label}
          startAdornment={getStartAdorment(props?.name ?? '')}
          endAdornment={getEndAdorment(props?.name ?? '')}
          aria-describedby="outlined-weight-helper-text"
          sx={{
            backgroundColor: 'white',
            paddingY: props?.multiline === true ? 1 : 0,
            paddingLeft: props?.multiline === true ? 1.5 : 0,
            borderRadius: 2,
            WebkitBoxShadow: 'red',
            '&.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
              borderColor: BLUE_GRAY_200,
            },
            '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
              {
                borderWidth: 1.5,
                borderColor: PRIMARY,
              },
            '&.MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline':
              {
                borderWidth: 1.5,
                borderColor: ERROR,
              },
            '& .MuiInputBase-input::placeholder': { fontSize: '0.9rem' },
          }}
          inputProps={{
            maxLength: props?.maxLength ? props?.maxLength : 50,
            style: { WebkitBoxShadow: '0 0 0 1000px white inset' },
          }}
          name={props.name}
          type={props?.type}
          value={props.value}
          error={error}
          placeholder={
            props.placeHolder ? props?.placeHolder : t(props?.label || '')
          }
          disabled={props.disabled ? props.disabled : false}
          size={'small'}
          multiline={props?.multiline ? props?.multiline : false}
          rows={props?.rows ? props?.rows : 1}
          autoComplete={'new-password'}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            validates(
              event.target?.value,
              props?.regexPattern,
              props?.label,
              props?.helperMessage,
              props?.isWorkNumber
            );
            if (props?.onChange) props?.onChange(event);
          }}
        />
        <FormHelperText
          id="outlined-weight-helper-text"
          sx={{
            '&.MuiFormHelperText-root': {
              margin: 0,
              paddingLeft: 1,
              color: props?.label === t('TAX_DESCRIPTION') ? GRAY_500 : ERROR,
              fontSize: '0.9rem',
            },
          }}
        >
          {helperText}
        </FormHelperText>
      </FormControl>
    );
  }

  function getStartAdorment(propertyName: string) {
    switch (propertyName) {
      case 'email':
        return <EmailOutlined sx={startAdormentStyle} />;
      case 'password':
        return <LockOutlined sx={startAdormentStyle} />;
      case 'search':
        return (
          <IconButton sx={startAdormentStyle} onClick={handleIconClick}>
            <Search />
          </IconButton>
        );
    }
  }

  function getEndAdorment(propertyName: string) {
    switch (propertyName) {
      case 'password':
        return (
          <IconButton sx={endAdormentStyle} onClick={props?.onIconClick}>
            {props?.showPassword ? (
              <Visibility sx={{ fontSize: 20, color: BLUE_GRAY_500 }} />
            ) : (
              <VisibilityOff sx={{ fontSize: 20, color: BLUE_GRAY_500 }} />
            )}
          </IconButton>
        );
      case 'search':
        return (
          <IconButton sx={endAdormentStyle} onClick={handleIconClick}>
            <Search />
          </IconButton>
        );
    }
  }

  function validate(target: HTMLInputElement | HTMLTextAreaElement) {
    switch (target.name) {
      case 'phone':
        if (ValidateMobile(target.value)) {
          setHelperText('');
          setError(false);
        } else {
          setHelperText('Please enter valid mobile number');
          setError(true);
        }
        break;
      case 'work':
        if (ValidateMobile(target.value)) {
          setHelperText('');
          setError(false);
        } else {
          setHelperText('Please Enter Valid work number');
          setError(true);
        }
        break;
      // case 'PinCode':
      //   if (ValidatePincode(target.value)) {
      //     setHelperText('');
      //     setError(false);
      //   } else {
      //     setHelperText('Please Enter Valid Pin Code');
      //     setError(true);
      //   }
      //   break;
      case 'email':
        if (ValidateEmail(target.value)) {
          setHelperText('');
          setError(false);
        } else {
          setHelperText('Please enter valid email Id');
          setError(true);
        }
        break;
      case 'address':
        if (ValidateAddress(target.value)) {
          setHelperText('');
          setError(false);
        } else {
          setHelperText('Please Enter Valid Address');
          setError(true);
        }
        break;
      case 'accountNumber':
        if (ValidateAccountNumber(target.value)) {
          setHelperText('');
          setError(false);
        } else {
          setHelperText('Please Enter Valid Account Number');
          setError(true);
        }
        break;
      case 'search':
      case 'alphaNumeric':
      case 'accountingCode':
      case 'password':
        break;
      default:
        break;
    }
  }

  function validates(
    value: string,
    regexPattern: RegExp | undefined,
    label: string | undefined,
    helperMessage: string | undefined,
    isWorkNumber?: boolean | undefined
  ) {
    let val: any = value;
    if (isWorkNumber === true) {
      if (value?.endsWith('-')) {
        val = val.substring(0, value.length - 1);
      } else {
        const input = value.replace(/\D/g, '').substring(0, 15);
        val = prepareMobileNumber(input);
      }
    }

    if (regexPattern) {
      let isValid = ValidateInput(regexPattern!, val);
      setError(!isValid);
      //  if(helperText !== undefined || helperText !== null || helperText !== "" )
      //  {
      //  setHelperText(isValid ? '' : helperText!)
      //  } else {
      //  }

      let helperMsg =
        helperMessage !== undefined
          ? helperMessage
          : t('PLEASE_ENTER_VALID') + _.toLower(label);
      setHelperText(isValid ? '' : helperMsg!);
    }
  }
}

export default BakeasyTextField;
